<template>
    <v-form data-cy="product-photo-form">
        <v-row dense class="mb-4">
            <v-col>
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
        </v-row>
        <images-upload-form
            v-model="productImages"
            :images-in-a-row="6"
            :init-data="useInitData"
            @delete-image="onImageDelete"
        >
            <template v-if="productImages" v-slot:top-fields="{image}">
                <v-checkbox
                    v-model="image.mainImage"
                    hide-details
                    :label="image.mainImage ? $t('main_image') : ''"
                    density="compact"
                    class="pb-1"
                    :data-cy="`main-image-${image.id}`"
                    :readonly="isReadOnly"
                    :disabled="image.toDelete"
                    @update:model-value="changeMainImage(image.id)"
                />
            </template>
            <template v-if="productImages" v-slot:fields="{image}">
                <template v-if="isHotel">
                    <v-autocomplete
                        v-model="image.imageType"
                        :items="selectItems"
                        :label="$t('category')"
                        item-title="name"
                        item-value="id"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        :data-cy="`hotel-img-category-${image.id}`"
                        :readonly="isReadOnly"
                        :disabled="image.toDelete"
                        @update:model-value="updateImageType(image)"
                    />
                    <v-select
                        v-if="image.imageType === 'ROOMS' && useRoomTypes"
                        v-model="image.objectId"
                        :items="useRoomTypes"
                        item-title="name"
                        item-value="id"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        :data-cy="`room-type-${image.id}`"
                        :clearable="!isReadOnly"
                        :readonly="isReadOnly"
                        :disabled="image.toDelete"
                        @update:model-value="updateRoomType(image)"
                    />
                </template>
                <v-autocomplete
                    v-else
                    v-model="image.linkedEntity.id"
                    :items="useItems"
                    :label="isPackageTour ? `${$t('product_tabs.itinerary')} ${$t('day')}` : $t('category')"
                    item-title="name"
                    item-value="id"
                    density="compact"
                    variant="outlined"
                    color="primary"
                    :data-cy="`category-${image.id}`"
                    clearable
                />
                <TextFieldLocale
                    v-model="image.title[activeLangCode]"
                    :active-lang-code="activeLangCode"
                    :data-cy="`title-${image.id}`"
                    :readonly="isReadOnly"
                    :disabled="image.toDelete"
                />
            </template>
            <template v-if="isHotel && productImages" v-slot:bottom-fields="{image}">
                <template v-if="image.imageType === 'ROOMS' && useIsShowRoomTypeMainCheckbox(image.objectId)">
                    <v-checkbox
                        v-model="image.mainPhoto"
                        hide-details
                        :label="
                            $t('main_image_for_room', {
                                prop: useRoomTypeName(image.objectId),
                            })
                        "
                        density="compact"
                        class="pb-1"
                        :data-cy="`main-image-${image.id}`"
                        :readonly="isReadOnly"
                        :disabled="image.toDelete"
                        @update:model-value="changeMainPhoto(image)"
                    />
                </template>
            </template>
        </images-upload-form>
        <v-row no-gutters class="gc-4">
            <v-btn
                v-if="!isReadOnly"
                class="save-btn"
                :loading="loadingImagesSave"
                :disabled="loadingImages"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
            <v-btn
                data-cy="cancel"
                :disabled="loadingImages"
                class="cancel-btn"
                :to="{
                    name: 'supplierProducts',
                    hash: `#${
                        serviceType === 'ownExcursion' || serviceType === 'concert' ? 'activities' : serviceType
                    }`,
                }"
                variant="text"
            >
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import ImagesUploadForm from '~src/components/account/snippets/ImagesUploadForm.vue'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {contractServiceType} from '~/utils/types/types'
    import {useCatalogue, useFullContract} from '~/composables/form-helpers'
    import {clone} from '@/utils/helpers'

    const props = defineProps<{
        contract?: any
        serviceType: string
    }>()

    const loadingImages = ref<boolean>(false)
    const loadingImagesSave = ref<boolean>(false)
    const productImages = ref([])
    const activeLangCode = ref<string>('en')
    const isPackageTour = ref(props.serviceType === contractServiceType.customComplexTour)
    const isHotel = ref(props.serviceType === contractServiceType.ownHotel)
    const isExtras = ref(props.serviceType === contractServiceType.ownExtraService)
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
    const selectItems = ref([])
    const defaultImages = ref([])
    const itineraryDays = ref([])

    const app = useNuxtApp()
    const {t} = useI18n()

    if (isHotel.value) {
        watch(useHotelInfo, async () => {
            await loadHotelData()
        })
    }

    watch(useFullContract, async () => {
        if (isPackageTour.value) await loadItineraryDays()
    })

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
        if (isHotel.value) {
            if (supplierStore.hotelInfo?.hotelId) {
                await supplierStore.loadRoomTypeData()
                await loadHotelData()
            }
        } else {
            await loadData()
        }

        if (isPackageTour.value) {
            await loadItineraryDays()
        } else if (isExtras.value) {
            if (!useProductServices.value.length) await supplierStore.loadProductServices()
        } else if (isHotel.value) {
            const hotelImageTypes = ['ROOMS', 'OTHER']

            selectItems.value = hotelImageTypes.sort().map(value => ({id: value, name: t(`hotelImageTypes.${value}`)}))
        } else {
            if (!useCatalogue.value?.length) await supplierStore.loadCatalogue()
        }
    })

    const useItems = computed(() => {
        switch (props.serviceType) {
            case contractServiceType.ownExtraService:
                return useProductServices.value
            case contractServiceType.customComplexTour:
                return itineraryDays.value.map(({id, number}) => ({
                    id,
                    name: `${t('table_day')} ${number}`,
                }))
            default:
                return useCatalogue.value
        }
    })

    const useInitData = computed(() => {
        if (isHotel.value) {
            return {
                imageType: 'OTHER',
                title: {},
                mainImage: !productImages.value?.length,
                mainPhoto: false,
            }
        }
        return {
            title: {},
            mainImage: !productImages.value?.length,
            linkedEntity: {id: null, type: isPackageTour.value ? 'ITINERARY_DAY' : 'CATEGORY'},
        }
    })

    const isReadOnly = computed(() => {
        if (isHotelDescriptionMode && app.$conf.public.account.allowToEditExternalHotelDescription) {
            return false
        }

        return isHotelDescriptionMode
    })

    async function loadData() {
        try {
            loadingImages.value = true
            const {images} = await app.$api.ownProductContractImages.get({contractId: props.contract.id})
            productImages.value = images?.map(el => ({
                ...el,
                title: el.title || {},
                order: el.orderNumber,
                linkedEntity: el?.linkedEntity || {id: null, type: isPackageTour.value ? 'ITINERARY_DAY' : 'CATEGORY'},
                toDelete: false,
            }))
            defaultImages.value = clone(productImages.value)
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        } finally {
            loadingImages.value = false
        }
    }

    async function loadHotelData() {
        try {
            loadingImages.value = true
            const {images} = await app.$api.hotelDescriptionImages.get(supplierStore.hotelInfo?.hotelId, {
                locale: 'all',
            })
            productImages.value = images
                .filter(({imageType}) => imageType !== 'LOGO')
                ?.map(el => ({
                    ...el,
                    title: el.title || {},
                    order: el.orderNumber,
                    url: el.imageData,
                    toDelete: false,
                }))
                .sort((a, b) => a.orderNumber - b.orderNumber)
            defaultImages.value = clone(productImages.value)
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        } finally {
            loadingImages.value = false
        }
    }

    async function loadItineraryDays(): Promise<void> {
        try {
            const res = await app.$api.ownPackageContractsItineraryDay.get(props.contract.id)
            itineraryDays.value = res.itineraryDays
        } catch (e) {
            itineraryDays.value = []
        }
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    const save = async () => {
        const toDeleteImages = productImages.value.filter(el => !!el.toDelete && typeof el.id === 'number')
        productImages.value = productImages.value.filter(el => !el.toDelete)
        productImages.value.forEach((item, index) => {
            item.order = index + 1
        })

        const toSaveImages = productImages.value.filter(el => !el.toDelete && el.blob)

        const toUpdateImages = []
        const images = productImages.value.filter(el => typeof el.id === 'number' && !el.toDelete)
        images.map(el => {
            const cop = defaultImages.value.find(item => item.id === el.id)
            if (!isEqual(cop, el)) toUpdateImages.push(el)
        })

        if (toDeleteImages?.length || toSaveImages?.length || toUpdateImages?.length) {
            try {
                loadingImagesSave.value = true
                if (toDeleteImages?.length) await deleteImages(toDeleteImages)
                if (toSaveImages?.length) {
                    if (isHotel.value) {
                        await saveHotelImages(toSaveImages)
                    } else {
                        await saveImages(toSaveImages)
                    }
                }

                if (toUpdateImages?.length) await updateImages(toUpdateImages)
                app.$toast.success(t('success_message.saved_successfully'))
                if (isHotel.value) {
                    await loadHotelData()
                } else await loadData()
            } catch (e) {
                app.$toast.error(t('error_message.save_error'))
            } finally {
                loadingImagesSave.value = false
            }
        }
    }

    const deleteImages = async toDeleteImages => {
        for (let i = 0; i < toDeleteImages.length; i++) {
            if (isHotel.value) {
                await app.$api.hotelDescriptionImages.remove(supplierStore.hotelInfo?.hotelId, toDeleteImages[i].id)
            } else {
                await app.$api.ownProductContractImages.remove({
                    contractId: props.contract.id,
                    imageId: toDeleteImages[i].id,
                })
            }
        }
    }

    const saveHotelImages = async toSaveImages => {
        await Promise.all(
            toSaveImages.map(async el => {
                const base64Img = await blobToBase64(el.blob)
                const encodedData = base64Img.split('base64,')[1]
                const {imageType, title, objectId, mainPhoto, mainImage, order} = el
                const rq = {
                    imageType,
                    title,
                    imageData: encodedData,
                    objectId,
                    mainPhoto,
                    mainImage,
                    orderNumber: order,
                }
                await app.$api.hotelDescriptionImages.post(supplierStore.hotelInfo?.hotelId, rq)
            })
        )
    }

    const saveImages = async toSaveImages => {
        const resp = await Promise.all(
            toSaveImages.map(async el => {
                const formData = new FormData()
                formData.append('contractId', props.contract.id)
                formData.append('imageFile', el.blob, el.blob.name)
                return app.$api.ownProductContractImages.post(formData)
            })
        )
        if (resp?.length) {
            await Promise.all(
                resp.map(async (el, index) => {
                    const {
                        images: [_image],
                    } = el
                    const {order, mainImage, title, linkedEntity} = toSaveImages[index]
                    const rq = {
                        id: _image.id,
                        orderNumber: order,
                        mainImage,
                        title,
                        ...(linkedEntity.id && {linkedEntity}),
                    }
                    return app.$api.ownProductContractImages.put(props.contract.id, rq)
                })
            )
        }
    }

    const updateImages = async toUpdateImages => {
        for (let i = 0; i < toUpdateImages.length; i++) {
            if (isHotel.value) {
                const {imageType, title, imageData, objectId, mainPhoto, mainImage, order} = toUpdateImages[i]
                const rq = {imageType, title, imageData, objectId, mainPhoto, mainImage, orderNumber: order}
                await app.$api.hotelDescriptionImages.put(supplierStore.hotelInfo?.hotelId, toUpdateImages[i].id, rq)
            } else {
                const {id, order, mainImage, title, linkedEntity} = toUpdateImages[i]
                const rq = {id, mainImage, title, orderNumber: order, ...(linkedEntity.id && {linkedEntity})}
                await app.$api.ownProductContractImages.put(props.contract.id, rq)
            }
        }
    }

    const changeMainImage = id => {
        const index = productImages.value.findIndex(el => el.id !== id && el.mainImage)
        if (index > -1) {
            productImages.value[index].mainImage = false
        } else productImages.value[0].mainImage = true
    }

    const changeMainPhoto = image => {
        if (!image?.mainPhoto) {
            image.mainPhoto = true
        } else {
            let reassignObjectId = image?.objectId
            productImages.value = productImages.value.map(item => {
                if (reassignObjectId === item?.objectId) {
                    return {
                        ...item,
                        mainPhoto: item?.order === image?.order,
                    }
                }
                return item
            })
        }
    }

    const useRoomTypes = computed(() => {
        // Filter room types to show only BASIC sustainability level rooms
        return supplierStore.roomTypes.filter(roomType => {
            const sustainabilityLevel = roomType.sustainabilityLevel || 'BASIC'
            return sustainabilityLevel === 'BASIC'
        })
    })

    const useRoomTypeName = computed(() => roomTypeId => {
        return useRoomTypes.value.find(({id}) => roomTypeId === id)?.name
    })

    const useIsShowRoomTypeMainCheckbox = computed(() => roomTypeId => {
        const roomsLength = productImages.value
            .filter(({objectId}) => objectId)
            .filter(({objectId}) => objectId === roomTypeId)?.length
        return roomsLength > 1
    })

    const updateImageType = image => {
        if (image?.imageType !== 'ROOMS' && image?.objectId) {
            let reassignObjectId = image?.objectId
            productImages.value = productImages.value.map(item => {
                if (item?.imageType !== 'ROOMS' && item?.objectId) {
                    return {
                        ...item,
                        objectId: null,
                        mainPhoto: false,
                    }
                }
                return item
            })
            if (reassignObjectId && !isContainMainPhoto(reassignObjectId)) reassignMainPhotoOnFirst(reassignObjectId)
        }
    }

    const isContainMainPhoto = roomTypeId => {
        const imgWithRoomType = productImages.value.filter(({objectId}) => objectId)
        const isContainMainPhoto = imgWithRoomType.filter(
            ({objectId, mainPhoto}) => objectId === roomTypeId && mainPhoto
        )
        return !!isContainMainPhoto.length
    }

    const reassignMainPhotoOnFirst = roomTypeId => {
        const index = productImages.value.findIndex(({objectId, toDelete}) => objectId === roomTypeId && !toDelete)
        if (index > -1) {
            productImages.value[index].mainPhoto = true
        }
    }

    const updateRoomType = image => {
        if ((image.mainPhoto && isContainMainPhoto(image?.objectId)) || !image?.objectId) {
            image.mainPhoto = false
        } else if (image?.objectId && !isContainMainPhoto(image?.objectId)) {
            reassignMainPhotoOnFirst(image?.objectId)
        }

        let groupedByObjectId = productImages.value.reduce((acc, item) => {
            if (item.objectId) {
                if (!acc[item.objectId]) {
                    acc[item.objectId] = []
                }
                acc[item.objectId].push(item)
            }
            return acc
        }, {})
        Object.values(groupedByObjectId).forEach(group => {
            let hasMainPhoto = group.some(item => item.mainPhoto)
            if (!hasMainPhoto) {
                let firstNonDeleted = group.find(item => !item.toDelete)
                if (firstNonDeleted) {
                    firstNonDeleted.mainPhoto = true
                }
            }
        })
    }

    const onImageDelete = image => {
        if (isHotel.value && image?.mainPhoto) {
            image.mainPhoto = false
            reassignMainPhotoOnFirst(image?.objectId)
        }
        if (image?.mainImage) {
            image.mainImage = false
            const index = productImages.value.findIndex(({mainImage, toDelete}) => !mainImage && !toDelete)
            if (index > -1) {
                productImages.value[index].mainImage = true
            }
        }
    }
</script>
